/**
 * liveCheckpoint — ce que le live de l'élève montre de Sam, en règles pures : la conversation de
 * checkpoint qui se pose, et l'ouverture que l'élève demande en touchant Sam. Le crochet du live
 * (`useStudentLive`) les lit ; elles se testent sans la salle.
 *
 * **Sam n'est pas là en Dialogue** (Wajih, 20/10) : l'élève parle avec son masteur. Une conversation
 * en cours se ferme au passage en Dialogue (le crochet la dit interrompue au serveur, qui garde ce qui
 * a déjà été répondu), et aucune ne se pose ni ne s'ouvre tant que le Dialogue dure.
 *
 * **Le panneau de Sam se pose dès le toucher** : la création de la conversation attend la première
 * phrase de Sam (le service d'IA, plusieurs secondes). Pendant ce temps le panneau est là, avec sa
 * forme (`opening`). Refermé avant que Sam ait parlé (`cancelled`), la conversation qui arrive se dit
 * interrompue et ne se montre pas ; retouché ensuite, seule la dernière demande compte. Une création
 * qui échoue se dit dans le panneau (`failed`), avec sa croix.
 */

/** Une conversation de checkpoint telle que le serveur la rend (`POST conversations`). */
export type LiveCheckpointConversation = {
    conversation?: { id?: number | string; status?: string; trigger_context?: string; activity_id?: number | null }
} & Record<string, unknown>

/** Les checkpoints que l'écran du live pose : la main levée et le silence de dix minutes. */
export const LIVE_CHECKPOINT_CONTEXTS = ["raise_hand", "silent_mode_10min"]

/** Ce qui ouvre une conversation de Sam : son contexte et sa source, comme le serveur les range. */
export type LiveCheckpointTrigger = { context: string; source: string }

/**
 * Sam touché par l'élève lui-même (la tuile, la bulle) : la conversation de la main levée, sans nouveau
 * type de checkpoint, mais sous une source à elle (`conversations.trigger_source`). Elle ne se confond
 * ni avec la main levée que le tuteur accepte (`tutor_accept`), ni avec Sam envoyé de ses événements
 * (`tutor_events_panel`), ni avec un checkpoint de démarrage (`automatic`).
 */
export const STUDENT_TAP_TRIGGER: LiveCheckpointTrigger = { context: "raise_hand", source: "student_tap" }

/** Le corps de la création d'une conversation de Sam en live (`POST live-sessions/conversations`). */
export function checkpointCreationBody(args: {
    studentId: number | string
    liveSessionId: number | string
    activityId: number | null
    trigger: LiveCheckpointTrigger
}) {
    return {
        student_id: args.studentId,
        live_session_id: args.liveSessionId,
        activity: args.activityId ?? undefined,
        trigger_context: args.trigger.context,
        trigger_source: args.trigger.source,
    }
}

/** L'ouverture demandée par l'élève : rien, Sam prépare, refermée en route, ou en échec. */
export type CheckpointOpening = "idle" | "opening" | "cancelled" | "failed"

/**
 * L'ouverture et le numéro de la dernière demande. Refermé puis retouché avant la première phrase, deux
 * créations sont en route : seul le retour de la dernière compte. L'autre est abandonnée
 * (`creationAbandoned`), et son retour ne range pas l'ouverture de la suivante.
 */
export type CheckpointOpeningState = { phase: CheckpointOpening; request: number }

export const initialCheckpointOpening: CheckpointOpeningState = { phase: "idle", request: 0 }

export type CheckpointOpeningEvent =
    /** Sam touché : la création part, sous un nouveau numéro. */
    | { type: "requested" }
    /** La création `request` est revenue (la conversation est rangée, ou elle a été dite interrompue). */
    | { type: "settled"; request: number }
    /** La création `request` a échoué. */
    | { type: "failed"; request: number }
    /** Le panneau refermé (le voile, la croix) ou le passage en Dialogue. */
    | { type: "dismissed" }

export function checkpointOpeningReducer(
    state: CheckpointOpeningState,
    event: CheckpointOpeningEvent
): CheckpointOpeningState {
    switch (event.type) {
        case "requested":
            return state.phase === "opening" ? state : { phase: "opening", request: state.request + 1 }
        case "settled":
            return event.request === state.request ? { ...state, phase: "idle" } : state
        case "failed":
            if (event.request !== state.request) return state
            // Refermé avant l'échec : l'élève ne l'attend plus, rien à dire.
            return { ...state, phase: state.phase === "cancelled" ? "idle" : "failed" }
        case "dismissed":
            if (state.phase === "opening") return { ...state, phase: "cancelled" }
            return state.phase === "failed" ? { ...state, phase: "idle" } : state
    }
}

/** La création `request`, revenue : l'élève l'a refermée, ou une demande plus récente l'a remplacée. */
export function creationAbandoned(state: CheckpointOpeningState, request: number): boolean {
    return request !== state.request || state.phase === "cancelled"
}

/** La conversation à poser, ou `null`. */
export function liveCheckpointToShow(args: {
    /** La salle a le drapeau `checkpoint-rh`. */
    enabled: boolean
    /** Celle que le magasin tient (`liveMode.checkpointConversation`). */
    conversation: LiveCheckpointConversation | null
    /** Celle qu'une séance d'avant avait rangée (le magasin est persisté) : elle ne se rouvre pas. */
    staleId: unknown
    /** Le tuteur a mis l'élève en Dialogue. */
    dialogue: boolean
    opening: CheckpointOpening
}): LiveCheckpointConversation | null {
    const { enabled, conversation, staleId, dialogue, opening } = args
    if (!enabled || conversation == null || dialogue || opening === "cancelled") return null
    if (conversation.conversation?.id === staleId) return null
    const context = conversation.conversation?.trigger_context
    return context != null && LIVE_CHECKPOINT_CONTEXTS.includes(context) ? conversation : null
}

/**
 * Sam envoyé par le tuteur : il passe devant et ouvre sa conversation (`open`), ou garde celle qui est
 * déjà ouverte (`keep`). Il ne passe pas devant dans une salle sans conversation de Sam, ni en
 * Dialogue, où sa conversation ne s'ouvrirait pas : il resterait devant sans rien ouvrir.
 *
 * **La main levée se baisse dans tous les cas** (`lowerHand`), comme l'ancien écouteur
 * (`useLiveKitEventListener`, `checkpointTriggerResponse` accepté : `raised: false`, toujours) : le
 * tuteur a répondu à la main, elle n'attend plus (Wajih, 20/10 : « comme sur l'ancien espace »).
 */
export function samSentOutcome(args: {
    checkpointsEnabled: boolean
    dialogue: boolean
    conversationOpen: boolean
    handRaised: boolean
}): { outcome: "open" | "keep" | "in_dialogue" | "without_checkpoints"; lowerHand: boolean } {
    const outcome = !args.checkpointsEnabled
        ? "without_checkpoints"
        : args.dialogue
          ? "in_dialogue"
          : args.conversationOpen
            ? "keep"
            : "open"
    return { outcome, lowerHand: args.handRaised }
}

/**
 * Sam envoyé pendant le quiz de prérequis (Wajih, 20/10) : il attend la fin du quiz pour s'ouvrir, sans
 * passer devant ni voiler la visio, et sans créer sa conversation, que la porte du quiz cacherait. Le
 * quiz fini, l'envoi gardé suit son chemin (`samSentOutcome`, relu à ce moment-là : une conversation
 * ouverte entre-temps est gardée). Il tombe, sans rien ouvrir, si le tuteur pousse une activité (sa
 * poussée ferme aussi une conversation de Sam), si l'élève passe en Dialogue (Sam n'y est pas), si le
 * live se termine ou si l'élève le quitte. Rien n'est créé au serveur tant qu'il attend : il ne laisse
 * aucune conversation en cours derrière lui.
 */
export type SamHold<E = unknown> = { held: boolean; pending: E | null }

export const initialSamHold: SamHold<null> = { held: false, pending: null }

export type SamHoldEvent<E = unknown> =
    /** Le quiz de prérequis tient la place (`true`), ou ne la tient plus. */
    | { type: "held"; held: boolean }
    /** Le tuteur envoie Sam. */
    | { type: "sent"; event: E }
    | { type: "dropped"; reason: "pushed" | "dialogue" | "ended" | "left" }

/** L'envoi à jouer maintenant (`fire`), ou `null`. */
export function samHoldReducer<E>(state: SamHold<E>, event: SamHoldEvent<E>): { state: SamHold<E>; fire: E | null } {
    switch (event.type) {
        case "held":
            if (event.held) return { state: { ...state, held: true }, fire: null }
            return { state: { held: false, pending: null }, fire: state.pending }
        case "sent":
            if (state.held) return { state: { ...state, pending: event.event }, fire: null }
            return { state, fire: event.event }
        case "dropped":
            return { state: { ...state, pending: null }, fire: null }
    }
}
